import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, CheckCircle } from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import MainTabsNav from '../components/MainTabsNav';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email) {
      setError('이메일을 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      // 가입 여부와 무관하게 항상 동일한 안내를 보여준다 (계정 존재 여부 노출 방지)
      await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      setSent(true);
    } catch (err) {
      console.error('Reset password request error:', err);
      setError('요청 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <MainTabsNav />
      <div className="flex items-center justify-center py-8 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img src="/logo/stampfit-logo.png" alt="StampFit" className="w-[180px] mx-auto" />
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-soft p-8">
          {sent ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-xl font-bold text-gray-900 mb-2">이메일을 확인해주세요</h1>
              <p className="text-gray-500 text-sm mb-6">
                입력하신 이메일로 비밀번호 재설정 링크를 보내드렸습니다.
                <br />
                이메일이 도착하지 않았다면 가입 여부와 스팸함을 확인해주세요.
              </p>
              <Link
                to="/login"
                className="block w-full font-semibold px-6 py-3 rounded-lg bg-primary hover:bg-primary-600 text-white transition-colors"
              >
                로그인으로 돌아가기
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">비밀번호 찾기</h1>
              <p className="text-gray-500 text-center text-sm mb-8">
                가입하신 이메일 주소를 입력하시면
                <br />
                비밀번호 재설정 링크를 보내드립니다.
              </p>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm text-center">{error}</p>
                </div>
              )}

              <form className="space-y-4" onSubmit={handleSubmit}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">이메일</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="email@example.com"
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none transition-all"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full font-semibold px-6 py-3 rounded-lg transition-colors ${
                    loading
                      ? 'bg-gray-400 cursor-not-allowed text-white'
                      : 'bg-primary hover:bg-primary-600 text-white'
                  }`}
                >
                  {loading ? '전송 중...' : '재설정 링크 보내기'}
                </button>
              </form>

              <div className="mt-6 flex items-center justify-center gap-3 text-sm text-gray-500">
                <Link to="/find-id" className="hover:text-primary hover:underline">
                  아이디 찾기
                </Link>
                <span className="text-gray-300">|</span>
                <Link to="/login" className="hover:text-primary hover:underline">
                  로그인으로 돌아가기
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
      </div>
    </>
  );
}
